'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

const LeafIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
);

function getRoleDashboard(role?: string): string {
  if (role === 'super_admin') return '/dashboard/super-admin';
  if (role === 'school_admin') return '/dashboard/school-admin';
  if (role === 'teacher') return '/dashboard/teacher';
  if (role === 'student') return '/dashboard/student';
  if (role === 'volunteer') return '/dashboard/volunteer';
  return '/dashboard';
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const { signIn, signOut, profile, user, loading: authLoading } = useAuth();
  const router = useRouter();

  // Fix: Clear any stale session state on mount to prevent cache-related login issues
  useEffect(() => {
    // If there's no active user but stale localStorage/cookie data exists, clear it
    const supabase = createClient();
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        // Clear any stale Supabase storage keys that may cause re-login issues
        try {
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('sb-') || key.startsWith('sb_')) {
              localStorage.removeItem(key);
            }
          });
        } catch {
          // localStorage not available — ignore
        }
      }
    });
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (!authLoading && user && profile) {
      // Super admin and staff roles are never blocked by pending status
      const isStaffRole = ['super_admin', 'school_admin', 'teacher', 'volunteer'].includes(profile.role ?? '');
      if (isStaffRole) {
        router.replace(getRoleDashboard(profile.role));
        return;
      }
      // Students with pending status stay on login page
      if (profile.role === 'student' && profile.account_status === 'pending') {
        return;
      }
      router.replace(getRoleDashboard(profile.role));
    }
  }, [authLoading, user, profile, router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Fix: Sign out any existing session before signing in to prevent stale session issues
      const supabase = createClient();
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      if (existingSession) {
        await supabase.auth.signOut();
      }
      await signIn(email, password);
      // Redirect handled by useEffect above after profile loads
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
      });
      if (error) throw error;
      setForgotMessage('Password reset email sent! Check your inbox and follow the link to reset your password.');
    } catch (err: unknown) {
      setForgotError(err instanceof Error ? err.message : 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Show pending approval screen ONLY for students awaiting review
  if (!authLoading && user && profile && profile.role === 'student' && profile.account_status === 'pending') {
    return (
      <div className="min-h-screen grain bg-eco-bg flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 rounded-full bg-amber-100 grid place-items-center mx-auto mb-6">
            <span className="text-4xl">⏳</span>
          </div>
          <h1 className="font-display text-2xl text-emerald-900 mb-3">Account Pending Approval</h1>
          <div className="eco-card p-6 text-left mb-6">
            <p className="text-slate-700 text-sm leading-relaxed">
              Thank you for registering. Your request has been submitted for approval. You will receive access once your school and account are verified.
            </p>
            <div className="mt-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <span className="text-amber-500 text-lg">📋</span>
              <div>
                <p className="text-sm font-semibold text-amber-800">Awaiting School Admin Review</p>
                <p className="text-xs text-amber-600 mt-0.5">Your School Admin will approve your account shortly.</p>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Signed in as: <span className="font-medium">{profile.email}</span>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="text-sm text-slate-500 hover:text-slate-700 underline"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain bg-eco-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex items-center gap-2 justify-center">
            <div className="w-10 h-10 rounded-2xl bg-emerald-600 grid place-items-center text-white shadow-md">
              <LeafIcon />
            </div>
            <span className="font-display text-2xl text-emerald-900">We Are Circular</span>
          </Link>
          <p className="mt-2 text-slate-600 text-sm">Sign in to your account</p>
        </div>

        {/* Login Card */}
        <div className="eco-card p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="mt-3 text-right">
            <button
              type="button"
              onClick={() => { setShowForgot(true); setForgotEmail(email); setForgotMessage(''); setForgotError(''); }}
              className="text-sm text-emerald-700 hover:underline font-medium"
            >
              Forgot password?
            </button>
          </div>

          <p className="mt-4 text-center text-sm text-slate-600">
            Don&apos;t have an account?{' '}
            <Link href="/register" className="text-emerald-700 font-semibold hover:underline">
              Register here
            </Link>
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm eco-card p-8 relative">
            <button
              onClick={() => setShowForgot(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 text-xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
            <h2 className="font-display text-xl text-emerald-900 mb-1">Reset Password</h2>
            <p className="text-sm text-slate-600 mb-5">Enter your email and we&apos;ll send you a reset link.</p>

            {forgotMessage ? (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 text-sm mb-4">
                {forgotMessage}
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-emerald-900 mb-1.5">Email address</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-eco-border bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                    placeholder="you@example.com"
                  />
                </div>
                {forgotError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                    {forgotError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {forgotLoading ? 'Sending…' : 'Send Reset Link'}
                </button>
              </form>
            )}

            <button
              onClick={() => setShowForgot(false)}
              className="mt-4 w-full text-center text-sm text-slate-500 hover:text-slate-700"
            >
              Back to login
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
